import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { QuickFilterOptions } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { BulkCollectionTabCallout } from '../../Collections/CollectionsList/callouts/bulk-collections-tab-callout';

export type StatusTabsProps = {
  rightContent?: React.ReactNode;
  selectedStatus: QuickFilterOptions;
  showCollectionTab?: boolean;
  showCollectionTabCallout?: boolean;
  onChange: (selectedStatus: QuickFilterOptions) => void;
  onDismissBanner?: () => void;
};

const styles = {
  statusTabs: css`
    margin-bottom: ${theme.spacing(3)};
  `,
  tab: css`
    font-size: ${theme.font.size.medium};
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
  `,
  rightContent: css`
    margin-left: auto;
    padding: 0 var(--fabric-space-gap-md);
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
};

const baseTrackingId = 'pay-portal-invoices--status-tabs--';

const tabs = (showCollectionTab?: boolean, rightContent?: React.ReactNode) => {
  const tabs = [];
  if (showCollectionTab) {
    tabs.push({ id: 'collections', label: 'Collections' });
  }
  tabs.push(
    ...[
      { id: 'allInvoices', label: 'All Requests' },
      { id: 'paid', label: 'Paid' },
      { id: 'unpaid', label: 'Unpaid' },
      { id: 'refunded', label: 'Refunds' },
      { id: 'failed', label: 'Failed' },
    ]
  );

  if (rightContent) {
    tabs.push({ type: 'right-content' });
  }
  return tabs;
};

export const StatusTabs = ({
  rightContent,
  selectedStatus,
  showCollectionTab,
  showCollectionTabCallout,
  onChange,
  onDismissBanner,
}: StatusTabsProps) => {
  const { t } = useTranslation('payments');
  const ref = useRef<HTMLDivElement>(null);
  const { shouldShowTabCallout, setShouldShowTabCallout, setShouldShowSelectAllCallout, dismissTabCallout } =
    useBulkCollectionsUIShallowStore(
      'shouldShowTabCallout',
      'setShouldShowTabCallout',
      'setShouldShowSelectAllCallout',
      'dismissTabCallout'
    );

  const handleOnChange = (id: string) => {
    if (id === 'collections') {
      setShouldShowSelectAllCallout(true);
    }
    onChange(id as QuickFilterOptions);
  };

  const handleCalloutDismiss = () => {
    dismissTabCallout();
    onDismissBanner?.();
  };

  useEffect(() => {
    // Sync tab callout state with prop
    setShouldShowTabCallout(!!showCollectionTabCallout);
  }, [showCollectionTabCallout]);

  return (
    <>
      <Tabs controlledTab={selectedStatus} onChange={handleOnChange}>
        <Tabs.Bar fullWidth={false} css={styles.statusTabs}>
          {tabs(showCollectionTab, rightContent).map(({ id, label, type }) => {
            if (id && label) {
              return (
                <Tabs.Tab key={id} trackingId={`${baseTrackingId}--${id}`} css={styles.tab} id={id}>
                  <div ref={id === 'collections' ? ref : undefined}>{t(label)}</div>
                </Tabs.Tab>
              );
            } else if (type === 'right-content') {
              return (
                <div key={type} css={styles.rightContent}>
                  {rightContent}
                </div>
              );
            }

            return null;
          })}
        </Tabs.Bar>
      </Tabs>
      {selectedStatus !== 'collections' && (
        <BulkCollectionTabCallout anchorRef={ref} showBanner={shouldShowTabCallout} onDismiss={handleCalloutDismiss} />
      )}
    </>
  );
};
